import React from 'react';

/**
 * bryekosis LMS brand mark for the student / teacher / parent app.
 *
 * Two lockups ship, and the right one depends on how much vertical room the slot
 * gives — not on taste:
 *
 *   full   "bryekosis" wordmark stacked above LMS. The wordmark is only ~12% of
 *          the lockup height, so under ~56px it turns into an unreadable smudge.
 *          Use it where there is room (login, register, access-denied).
 *   mark   LMS alone. The letters fill the full height, so it stays sharp in the
 *          small slots (sidebar, top bar).
 *
 * Before this component the five logo slots were five hand-written <img> tags
 * pointing at the same file; the threshold lives here so they cannot drift.
 *
 * `tone` picks the ink — 'color' on light surfaces, 'white' on dark or gradient
 * ones. Both lockups exist in both inks.
 */

/** Below this rendered height the "bryekosis" wordmark stops being legible. */
const FULL_LOCKUP_MIN_HEIGHT = 56;

export type BrandLogoVariant = 'full' | 'mark';
export type BrandLogoTone = 'color' | 'white';

const SRC: Record<BrandLogoVariant, Record<BrandLogoTone, string>> = {
  full: { color: '/bryekosis-lms-logo.png', white: '/bryekosis-lms-logo-white.png' },
  mark: { color: '/bryekosis-lms-mark.png', white: '/bryekosis-lms-mark-white.png' },
};

interface Props {
  /** Rendered height in px. Drives the variant unless `variant` is given. */
  height?: number;
  /** Tailwind height classes for responsive slots, e.g. "h-[36px] xl:h-[44px]".
   *  When set, pass `variant` too — the class can't be measured here. */
  className?: string;
  tone?: BrandLogoTone;
  variant?: BrandLogoVariant;
}

const BrandLogo: React.FC<Props> = ({ height, className, tone = 'color', variant }) => {
  const resolved: BrandLogoVariant =
    variant ?? (height != null && height >= FULL_LOCKUP_MIN_HEIGHT ? 'full' : 'mark');
  return (
    <img
      src={SRC[resolved][tone]}
      alt="bryekosis LMS"
      className={className ?? 'object-contain select-none'}
      style={height != null && !className ? { height, width: 'auto' } : undefined}
      draggable={false}
    />
  );
};

export default BrandLogo;
